/**
 * Smooth Scroll Utility
 * Provides methods for smooth scrolling to elements, coordinates, and automatic link handling
 *
 * <!-- These will automatically smooth scroll -->
 * <a href="#section-1">Go to Section 1</a>
 * <a href="#footer">Go to Footer</a>
 *
 * <!-- Or trigger with JavaScript -->
 * <button onclick="scrollDownPage()">Scroll Down</button>
 * <button onclick="scrollToElement('#content')">Jump to Content</button>
 */

class SmoothScroll {
	constructor( options = {} ) {
		this.defaultOptions = {
			duration: 800,
			easing: 'easeInOutQuart',
			offset: 0,
			behavior: 'smooth',
		};

		this.options = { ...this.defaultOptions, ...options };

		// Easing functions
		this.easingFunctions = {
			linear: ( t ) => t,
			easeInQuad: ( t ) => t * t,
			easeOutQuad: ( t ) => t * ( 2 - t ),
			easeInOutQuad: ( t ) =>
				t < 0.5 ? 2 * t * t : -1 + ( 4 - 2 * t ) * t,
			easeInCubic: ( t ) => t * t * t,
			easeOutCubic: ( t ) => --t * t * t + 1,
			easeInOutCubic: ( t ) =>
				t < 0.5
					? 4 * t * t * t
					: ( t - 1 ) * ( 2 * t - 2 ) * ( 2 * t - 2 ) + 1,
			easeInQuart: ( t ) => t * t * t * t,
			easeOutQuart: ( t ) => 1 - --t * t * t * t,
			easeInOutQuart: ( t ) =>
				t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,
		};
	}

	/**
	 * Scroll to a specific element
	 * @param {string|Element} target  CSS selector or DOM element
	 * @param {Object}         options Override default options
	 */
	scrollToElement( target, options = {} ) {
		const element =
			typeof target === 'string'
				? document.querySelector( target )
				: target;

		if ( ! element ) {
			console.warn( `SmoothScroll: Element "${ target }" not found` );
			return;
		}

		const targetPosition =
			element.getBoundingClientRect().top + window.pageYOffset;
		this.scrollToPosition( targetPosition, options );
	}

	/**
	 * Scroll to specific coordinates
	 * @param {number} targetY - Y coordinate to scroll to
	 * @param {Object} options - Override default options
	 */
	scrollToPosition( targetY, options = {} ) {
		const config = { ...this.options, ...options };
		const startY = window.pageYOffset;
		const distance = targetY - startY - config.offset;

		if ( Math.abs( distance ) < 2 ) return; // Too close, no need to scroll

		// Use native smooth scroll if supported and no custom easing
		if (
			config.behavior === 'smooth' &&
			'scrollBehavior' in document.documentElement.style
		) {
			window.scrollTo( {
				top: targetY - config.offset,
				behavior: 'smooth',
			} );
			return;
		}

		// Custom smooth scroll with easing
		let startTime;
		const easingFunction =
			this.easingFunctions[ config.easing ] ||
			this.easingFunctions.easeInOutQuart;

		const animateScroll = ( currentTime ) => {
			if ( ! startTime ) startTime = currentTime;

			const timeElapsed = currentTime - startTime;
			const progress = Math.min( timeElapsed / config.duration, 1 );
			const ease = easingFunction( progress );

			window.scrollTo( 0, startY + distance * ease );

			if ( progress < 1 ) {
				requestAnimationFrame( animateScroll );
			}
		};

		requestAnimationFrame( animateScroll );
	}

	/**
	 * Scroll to top of page
	 * @param {Object} options - Override default options
	 */
	scrollToTop( options = {} ) {
		this.scrollToPosition( 0, options );
	}

	/**
	 * Scroll down by viewport height
	 * @param {number} multiplier How many viewport heights to scroll (default: 1)
	 * @param {Object} options    Override default options
	 */
	scrollDownPage( multiplier = 1, options = {} ) {
		const currentY = window.pageYOffset;
		const viewportHeight = window.innerHeight;
		this.scrollToPosition(
			currentY + viewportHeight * multiplier,
			options
		);
	}

	/**
	 * Scroll up by viewport height
	 * @param {number} multiplier How many viewport heights to scroll (default: 1)
	 * @param {Object} options    Override default options
	 */
	scrollUpPage( multiplier = 1, options = {} ) {
		const currentY = window.pageYOffset;
		const viewportHeight = window.innerHeight;
		this.scrollToPosition(
			currentY - viewportHeight * multiplier,
			options
		);
	}

	/**
	 * Initialize automatic smooth scroll for anchor links
	 * @param {string} selector CSS selector for links to enhance (default: 'a[href^="#"]')
	 * @param {Object} options  Override default options
	 */
	initAutoScroll( selector = 'a[href^="#"]', options = {} ) {
		document.addEventListener( 'click', ( e ) => {
			const link = e.target.closest( selector );

			if ( ! link ) return;

			const href = link.getAttribute( 'href' );
			if ( ! href || href === '#' ) return;

			const target = document.querySelector( href );
			if ( ! target ) return;

			e.preventDefault();
			this.scrollToElement( target, options );

			// Update URL hash without jumping
			if ( history.pushState ) {
				history.pushState( null, null, href );
			}
		} );
	}
}

// Create default instance
const smoothScroll = new SmoothScroll();

// Export for module usage
export default SmoothScroll;

// Global convenience functions
window.scrollToElement = ( target, options ) =>
	smoothScroll.scrollToElement( target, options );
window.scrollToPosition = ( targetY, options ) =>
	smoothScroll.scrollToPosition( targetY, options );
window.scrollToTop = ( options ) => smoothScroll.scrollToTop( options );
window.scrollDownPage = ( multiplier, options ) =>
	smoothScroll.scrollDownPage( multiplier, options );
window.scrollUpPage = ( multiplier, options ) =>
	smoothScroll.scrollUpPage( multiplier, options );

// Initialize automatic smooth scrolling for hash links
document.addEventListener( 'DOMContentLoaded', () => {
	smoothScroll.initAutoScroll();
} );
